/**
 * Block 13 PR-G Phase 5A — Карточка договора в списке «Мои договоры» (Экран 2.13.1).
 *
 * Pure presenter — renders saved contract metadata + status badge + tap link на
 * `/contracts/:id/edit` (Phase 6 route wiring). Route не registered в Phase 5
 * → Phase 6 cleanup; здесь `<Link to>` syntactically valid, React Router
 * resolve'ит на runtime (404 если route не зарегистрирован, что и ожидается
 * до Phase 6).
 *
 * **Card layout (P-038 explicit bg-card, P-039 text-ink, P-041 no yellow accent):**
 *   - container: `bg-card border border-accent rounded-2xl` — no yellow в default
 *   - title: «{name}» (auto-generated «Договор № N» если есть, иначе fallback)
 *   - meta row: customerName (or '—' если пусто) + formattedDate
 *   - totalCost (formatCurrency per template.meta.currencyCode)
 *   - status badge: ready/sent variants — semantic colors через border tokens,
 *     не yellow accent. Draft badge не рендерится (Phase 7 hotfix R7-#3).
 *
 * **Phase 7 hotfix round 7 (R7-#2, 2026-05-26) — delete контракта:**
 * Trash icon button — absolute-positioned **sibling** Link (НЕ внутри!) в
 * relative wrapper. Click → opens DeleteContractDialog → confirm →
 * `onDelete(contract.id)` callback (Container вызывает `deleteSavedContract` +
 * refetch list). **MUST NOT** nest `<button>` inside `<a>` (HTML5 interactive
 * content rule — accessibility nightmare + hydration warnings + screen reader
 * confusion). Поэтому structure: `<div className="relative"><Link/>{trash}</div>`.
 *
 * Reference:
 *   - PRD §10.13.1 «карточки с номером договора, датой, ФИО клиента, статусом»
 *   - `apps/web/src/features/shops/ShopsListView.tsx` ShopCard pattern
 *   - ADR-0025 §Phase 5 file list
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Send, Trash2 } from 'lucide-react';

import { formatCurrency } from '../services/contractTextRender';
import { sumSpecCost } from '../state/contractSelectors';
import type { SavedContract } from '../state/savedContractsIdb';
import { DeleteContractDialog } from './DeleteContractDialog';

export interface ContractCardProps {
  contract: SavedContract;
  /**
   * Phase 7 hotfix round 7 (R7-#2): callback после confirm delete. Container
   * вызывает `deleteSavedContract(id)` + refetch list.
   */
  onDelete: (id: string) => void;
}

const STATUS_LABEL: Record<SavedContract['state']['status'], string> = {
  draft: 'Черновик',
  ready: 'Готов',
  sent: 'Отправлен',
};

function formatDateSimple(iso: string): string {
  // Простой format DD.MM.YYYY — для card meta (не legal text). Если ISO
  // невалиден — возвращаем '—' (i18n-safe fallback per B-149).
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return '—';
  return `${m[3]}.${m[2]}.${m[1]}`;
}

function StatusBadge({ status }: { status: SavedContract['state']['status'] }) {
  // Phase 7 hotfix round 7 (R7-#3, 2026-05-26): «Черновик» badge не рендерится
  // — Aibek visual verify round 12 finding. Все договоры в списке по умолчанию
  // draft, badge избыточен. STATUS_LABEL entry preserved для future ready/sent
  // flow (Phase 8+).
  if (status === 'draft') return null;
  const label = STATUS_LABEL[status];
  const Icon = status === 'ready' ? Check : Send;
  // P-041 compliance: bg-card + border tokens; no yellow accent.
  const tone = 'border-2 border-ink text-ink';
  return (
    <span
      data-testid={`contract-status-${status}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-card text-xs font-medium ${tone}`}
    >
      <Icon size={12} aria-hidden="true" />
      {label}
    </span>
  );
}

export function ContractCard({ contract, onDelete }: ContractCardProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const customerLine = contract.state.customerName.trim() || '—';
  const dateLine = formatDateSimple(contract.state.contractDate);
  // Phase 6.5B Amendment A1 (2026-05-24) — currency from draft.currencyCode
  // (was: template.meta.currencyCode). loadContractTemplate no longer needed
  // here — list card displays cost in user-selected currency.
  //
  // Phase 6.5G hotfix round 3 (R3-#1) — cost source switched от state.totalCost
  // (vestigial after read-only UI) к sumSpecCost helper (single source of truth
  // shared с UI / PDF render — см. contractSelectors.ts).
  const costFromSpec = sumSpecCost(contract.state.spec);
  const costLine =
    costFromSpec > 0 ? formatCurrency(costFromSpec, contract.state.currencyCode) : '—';
  const title =
    contract.name.trim() ||
    (contract.state.contractNum ? `Договор №${contract.state.contractNum}` : 'Новый договор');

  // Phase 7 hotfix round 7 (R7-#2): trash button — sibling Link в relative
  // wrapper (НЕ внутри Link, иначе HTML5 interactive content nesting violation
  // → screen reader confusion + hydration warnings). Click handler не нуждается
  // в preventDefault/stopPropagation т.к. button уже out of Link tree.
  function handleConfirmDelete(): void {
    setDeleteDialogOpen(false);
    onDelete(contract.id);
  }

  return (
    <>
      <div className="relative">
        <Link
          to={`/contracts/${contract.id}/edit`}
          data-testid={`contract-card-${contract.id}`}
          aria-label={`Открыть договор: ${title}`}
          className="block w-full rounded-2xl bg-card border-[1.5px] border-accent shadow-card p-4 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90 no-underline"
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3
              className="font-display text-base text-ink m-0 leading-tight truncate"
              data-testid="contract-card-title"
            >
              {title}
            </h3>
            <div className="flex items-center gap-2 shrink-0">
              <StatusBadge status={contract.state.status} />
              {/* Spacer для trash button overlay (absolute-positioned outside Link) */}
              <span aria-hidden="true" className="inline-block w-tap h-tap" />
            </div>
          </div>
          <p className="text-sm text-ink m-0 truncate" data-testid="contract-card-customer">
            Клиент: {customerLine}
          </p>
          <div className="flex items-center justify-between gap-2 mt-1">
            <span className="text-xs text-ink/60" data-testid="contract-card-date">
              {dateLine}
            </span>
            <span className="text-sm font-semibold text-ink" data-testid="contract-card-cost">
              {costLine}
            </span>
          </div>
        </Link>
        <button
          type="button"
          onClick={() => setDeleteDialogOpen(true)}
          aria-label={`Удалить договор: ${title}`}
          data-testid={`contract-card-delete-${contract.id}`}
          className="absolute top-3 right-3 min-h-tap min-w-tap inline-flex items-center justify-center rounded-xl text-ink/60 bg-card hover:text-ink hover:bg-canvas focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80 touch-manipulation"
        >
          <Trash2 size={18} aria-hidden="true" />
        </button>
      </div>
      <DeleteContractDialog
        isOpen={deleteDialogOpen}
        contractName={title}
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}
